import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function GraphqlBasics2Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          GraphQL Basics - Part 2
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Apollo Client 설정과 React에서의 GraphQL 사용법을 학습합니다. 
        실제 프로젝트에서 쿼리와 뮤테이션을 어떻게 사용하는지 알아보겠습니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Apollo Client 설정과 사용
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Apollo Client 설치 및 설정
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Apollo Client는 GraphQL을 위한 가장 인기 있는 클라이언트 라이브러리입니다. 
        캐싱, 에러 처리, 상태 관리를 모두 제공합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`# 설치
npm install @apollo/client graphql

# 기본 설정
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';

const client = new ApolloClient({
  uri: 'http://localhost:4000/graphql',
  cache: new InMemoryCache({
    typePolicies: {
      Product: {
        fields: {
          comments: {
            merge(existing = [], incoming) {
              return [...existing, ...incoming];
            }
          }
        }
      }
    }
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    }
  }
});

// 앱에 Apollo Provider 적용
function App() {
  return (
    <ApolloProvider client={client}>
      <MyComponent />
    </ApolloProvider>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        useQuery 훅 사용법
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        useQuery는 데이터를 조회할 때 사용하는 훅입니다. 로딩 상태, 에러 처리, 자동 리페치 등을 제공합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useQuery, gql } from '@apollo/client';
import { CircularProgress, Alert, Box } from '@mui/material';

// GraphQL 쿼리 정의
const GET_PRODUCTS = gql\`
  query GetProducts($filter: ProductFilter) {
    products(filter: $filter) {
      id
      name
      price
      category {
        id
        name
      }
      inventory {
        quantity
        inStock
      }
    }
  }
\`;

function ProductList() {
  const { 
    data, 
    loading, 
    error, 
    refetch 
  } = useQuery(GET_PRODUCTS, {
    variables: { 
      filter: { inStock: true } 
    },
    pollInterval: 30000,  // 30초마다 자동 리페치
    notifyOnNetworkStatusChange: true
  });

  // 로딩 상태 처리
  if (loading) return <CircularProgress />;
  
  // 에러 상태 처리
  if (error) {
    return (
      <Alert severity="error">
        데이터 로딩 중 오류가 발생했습니다: {error.message}
        <Button onClick={() => refetch()}>다시 시도</Button>
      </Alert>
    );
  }

  return (
    <Box>
      {data?.products.map(product => (
        <Card key={product.id}>
          <CardContent>
            <Typography variant="h6">{product.name}</Typography>
            <Typography>가격: ₩{product.price.toLocaleString()}</Typography>
            <Chip 
              label={product.inventory.inStock ? '재고 있음' : '품절'}
              color={product.inventory.inStock ? 'success' : 'error'}
            />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        useMutation 훅 사용법
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        useMutation은 데이터를 변경할 때 사용합니다. 낙관적 업데이트, 캐시 업데이트 등의 고급 기능을 제공합니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useMutation, gql } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';

const CREATE_PRODUCT = gql\`
  mutation CreateProduct($input: ProductInput!) {
    createProduct(input: $input) {
      id
      name
      price
      category {
        id
        name
      }
    }
  }
\`;

function CreateProductForm() {
  const { control, handleSubmit, reset } = useForm();
  
  const [createProduct, { 
    loading, 
    error, 
    data 
  }] = useMutation(CREATE_PRODUCT, {
    // 성공 시 콜백
    onCompleted: (data) => {
      console.log('상품 생성 완료:', data.createProduct);
      reset();
      toast.success('상품이 등록되었습니다!');
    },
    
    // 에러 시 콜백
    onError: (error) => {
      console.error('상품 생성 실패:', error);
      toast.error('상품 등록에 실패했습니다.');
    },
    
    // 캐시 업데이트
    update: (cache, { data: { createProduct } }) => {
      const existingProducts = cache.readQuery({
        query: GET_PRODUCTS
      });
      
      cache.writeQuery({
        query: GET_PRODUCTS,
        data: {
          products: [createProduct, ...existingProducts.products]
        }
      });
    },
    
    // 특정 쿼리 리페치
    refetchQueries: [
      { query: GET_PRODUCTS },
      'GetCategories'  // 쿼리 이름으로도 가능
    ]
  });

  const onSubmit = async (formData) => {
    try {
      await createProduct({
        variables: {
          input: formData
        }
      });
    } catch (err) {
      // 에러는 onError 콜백에서 처리됨
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="상품명"
            disabled={loading}
          />
        )}
      />
      
      <Button 
        type="submit" 
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
      >
        {loading ? '등록 중...' : '상품 등록'}
      </Button>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error.message}
        </Alert>
      )}
    </form>
  );
}`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/graphql-basics-1')}
        >
          ← PART 1: 기본 개념과 스키마
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/graphql-basics-3')}
        >
          PART 3: 실시간 데이터와 최적화 →
        </Button>
      </Box>
    </Container>
  );
}
